import {
  DetailedStxBalance,
  FtBalanceItem,
  FtTokenMetadataResponse,
  HistoryPeriod,
  NftBalanceItem,
  NftTokenMetadataResponse,
  SafeBalanceHistoryResponse,
  SafeBalanceResponse,
  TokenPriceResponse,
} from 'types/api/assetsApi';

import { STXAxiosInstance } from '../Axios/STXAxiosInstance';

const HIRO_BASE_URL: Record<'stx:testnet' | 'stx:mainnet', string> = {
  'stx:mainnet': 'https://api.hiro.so',
  'stx:testnet': 'https://api.testnet.hiro.so',
};

// Max page size accepted by the Hiro paginated balance endpoints.
const HIRO_PAGE_LIMIT = 200;

const fetchStxBalance = async (baseUrl: string, address: string): Promise<DetailedStxBalance> => {
  const response = await fetch(`${baseUrl}/extended/v2/addresses/${address}/balances/stx`);
  const data = (await response.json()) as Partial<DetailedStxBalance>;

  return {
    balance: data.balance ?? '0',
    total_sent: data.total_sent ?? '0',
    total_received: data.total_received ?? '0',
    total_fees_sent: data.total_fees_sent ?? '0',
    total_miner_rewards_received: data.total_miner_rewards_received ?? '0',
    lock_tx_id: data.lock_tx_id ?? '',
    locked: data.locked ?? '0',
    lock_height: data.lock_height ?? 0,
    burnchain_lock_height: data.burnchain_lock_height ?? 0,
    burnchain_unlock_height: data.burnchain_unlock_height ?? 0,
    estimated_balance: data.estimated_balance ?? data.balance ?? '0',
  };
};

const fetchFungibleTokens = async (baseUrl: string, address: string): Promise<FtBalanceItem[]> => {
  const results: { token: string; balance: string }[] = [];
  let offset = 0;
  let total = 0;

  do {
    const response = await fetch(
      `${baseUrl}/extended/v2/addresses/${address}/balances/ft?limit=${HIRO_PAGE_LIMIT}&offset=${offset}`
    );
    const data = (await response.json()) as {
      total?: number;
      results?: { token: string; balance: string }[];
    };
    results.push(...(data.results ?? []));
    total = data.total ?? 0;
    offset += HIRO_PAGE_LIMIT;
  } while (offset < total);

  return results.map((item) => ({
    token: item.token,
    contract: item.token.split('::')[0],
    balance: item.balance,
    total_sent: '0',
    total_received: '0',
    price: 0,
  }));
};

const fetchNonFungibleTokens = async (
  baseUrl: string,
  address: string
): Promise<NftBalanceItem[]> => {
  const counts: Record<string, number> = {};
  let offset = 0;
  let total = 0;

  do {
    const response = await fetch(
      `${baseUrl}/extended/v1/tokens/nft/holdings?principal=${address}&limit=${HIRO_PAGE_LIMIT}&offset=${offset}`
    );
    const data = (await response.json()) as {
      total?: number;
      results?: { asset_identifier: string }[];
    };
    (data.results ?? []).forEach((holding) => {
      counts[holding.asset_identifier] = (counts[holding.asset_identifier] ?? 0) + 1;
    });
    total = data.total ?? 0;
    offset += HIRO_PAGE_LIMIT;
  } while (offset < total);

  return Object.entries(counts).map(([token, count]) => ({
    token,
    contract: token.split('::')[0],
    count: count.toString(),
    total_sent: '0',
    total_received: '0',
  }));
};

// Fetches balances directly from the Hiro API and reshapes them into the same
// SafeBalanceResponse the Asigna backend used to return, so downstream consumers
// stay unchanged. The legacy /extended/v1/address/{addr}/balances endpoint is
// deprecated, so STX / FT / NFT are fetched from their v2 (and NFT holdings) replacements.
export const getMultisigBalance = async (
  address: string,
  network: 'stx:testnet' | 'stx:mainnet',
  options?: { includeNfts?: boolean }
): Promise<SafeBalanceResponse> => {
  const baseUrl = HIRO_BASE_URL[network];

  // NFT holdings are paginated and the heaviest part of the call. Callers that
  // only need STX / FT totals (e.g. sidebar balance polling) skip them.
  const includeNfts = options?.includeNfts ?? true;

  const [stx, fungible_tokens, non_fungible_tokens] = await Promise.all([
    fetchStxBalance(baseUrl, address),
    fetchFungibleTokens(baseUrl, address),
    includeNfts ? fetchNonFungibleTokens(baseUrl, address) : Promise.resolve<NftBalanceItem[]>([]),
  ]);

  return {
    result: {
      address,
      network,
      stx,
      fungible_tokens,
      non_fungible_tokens,
    },
  };
};

export const getMultisigBalanceHistory = async (
  address: string,
  network: 'stx:testnet' | 'stx:mainnet',
  type: HistoryPeriod
) =>
  STXAxiosInstance.get<SafeBalanceHistoryResponse>(
    `/balance-history/${address}/${network}/${type}`,
    {}
  ).then((data) => {
    return data.data;
  });

export const getTokenPrices = async () =>
  STXAxiosInstance.get<TokenPriceResponse>(`/balance/token-prices`, {}).then((data) => {
    return data.data;
  });

export const getFtTokenMetadata = async (token: string) =>
  STXAxiosInstance.get<FtTokenMetadataResponse>(`/metadata/ft/${token}`, {}).then((data) => {
    return data.data;
  });

export const getSftTokenMetadata = async (token: string) =>
  STXAxiosInstance.get<unknown>(`/metadata/sft/${token}`, {}).then((data) => {
    return data.data;
  });

export const getNftTokenMetadata = async (token: string) =>
  STXAxiosInstance.get<NftTokenMetadataResponse>(`/metadata/nft/${token}`, {}).then((data) => {
    return data.data;
  });
